<script setup lang="ts">
import type { Room } from '~~/shared/types/payload-types'
import { getFormattedYearRange } from '~/composables/get-formatted-date-range'

const props = defineProps<{
  room: Room,
  floor: string | null,
  floorRooms: Room[],
}>()

interface PersonOccurrenceDoc {
  id: string
  title: string
  dateStart: string
  dateEnd: string
  isOngoing?: boolean | null
  type: 'participant' | 'tutor' | 'staff'
  person?: { id: string; title: string; slug: string } | null
}

interface ProgrammeDoc {
  id: string
  title: string
  slug: string
  recurrences: { id?: string | null; dateTimeStart: string; dateTimeEnd: string }[]
  type?: { id: string; title: string; slug: string } | null
}

const occurrences = computed<PersonOccurrenceDoc[]>(() =>
  (props.room.personOccurrences?.docs ?? []).filter(
    (d: PersonOccurrenceDoc | string): d is PersonOccurrenceDoc => typeof d !== 'string',
  ),
)

const programme = computed<ProgrammeDoc[]>(() =>
  (props.room.programme?.docs ?? []).filter(
    (d: ProgrammeDoc | string): d is ProgrammeDoc => typeof d !== 'string',
  ),
)

// one row per person, with the years they were in this room -- labelled on
// years alone, the same way the people page labels its groups. A person who
// was here more than once lists each period, most recent first.
const people = computed(() => {
  const seen = new Map<string, { id: string; title: string; slug: string; periods: { label: string; sortEnd: number }[] }>()
  for (const occ of occurrences.value) {
    if (!occ.person) continue
    const label = getFormattedYearRange(occ.dateStart, occ.dateEnd, { isOngoing: Boolean(occ.isOngoing) })
    const end = occ.isOngoing ? Number.POSITIVE_INFINITY : new Date(occ.dateEnd ?? occ.dateStart).getTime()
    const entry = seen.get(occ.person.id) ?? { ...occ.person, periods: [] }
    if (label && !entry.periods.some(p => p.label === label)) {
      entry.periods.push({ label, sortEnd: Number.isNaN(end) ? 0 : end })
    }
    seen.set(occ.person.id, entry)
  }
  return [...seen.values()].map(person => ({
    ...person,
    years: person.periods.sort((a, b) => b.sortEnd - a.sortEnd).map(p => p.label).join(', '),
  }))
})
</script>

<template>
  <div>
    <div 
      v-if="hasRichTextContent(room?.description)"
      class="mb-3 container-d px-1"
    >
      <core-rich-text
        :content="room.description"
      />
    </div>

    <div
      class="mx-1"
      :class="{
        'grid grid-cols-1 gap-1': people.length || programme.length,
      }"
    >
      <div v-if="people.length || programme.length" class="flex flex-col gap-2">
        <div
          v-if="people.length"
        >
          <div >
            {{ $t('labels.people', { count: people.length }) }}
          </div>
          <ul>
            <li v-for="person in people" :key="person.id">
              <shared-person-link :name="person.title" :slug="person.slug" />
              <span v-if="person.years"> ({{ person.years }})</span>
            </li>
          </ul>
        </div>

        <div
          v-if="programme.length"
        >
          <div
          >
            {{ $t('labels.programme') }}
          </div>

          <ul class="flex flex-col gap-0_35">
            <li v-for="prog in programme" :key="prog.id">
              <site-link
                :route-object="{
                  name: 'programme-type-slug',
                  params: { type: prog.type?.slug, slug: prog.slug },
                }"
                class="text-hover-accent"
              >
                {{ prog.title }}
              </site-link>
              
              <ul v-if="prog.recurrences?.length" class="text-sm text-gray-500">
                <li v-for="(recurrence, index) in getFormattedRecurrences(prog.recurrences)" :key="index">
                  {{ recurrence }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="room?.media?.docs?.length"
      >
        <core-media-preview
          :media="room?.media?.docs"
          :limit="8"
        />
      </div>
    </div>

    <floorplan-floor
      class="m-1 mt-3"
      v-if="floor"
      :floor="floor"
      :rooms="props.floorRooms"
      :selected-slug="room.slug"
    />
  </div>
</template>
